import { DecoderFct } from '@iotize/common/converter/api';
import {
  ServiceCallRunner,
  ServiceCallType,
  TapError,
  TapResponse,
} from '@iotize/tap';
import { BodyDecoder } from '@iotize/tap/client/api';

import { MultiRequestFrame, MultiResponseFrame } from '../generated/models';
import { InterfaceService } from '../generated/service';

type ExecuteMutlipleCallItem<BodyType, ReturnType> =
  | ServiceCallType<BodyType, ReturnType>
  | {
      call: ServiceCallType<BodyType, ReturnType>;
      expectedResponseStatus: number;
    };
// type ExecuteMultipleCallsParameterType<BodyType, ReturnType> = ExecuteMutlipleCallItem<>[];

const TAP_REQUEST_FRAME_HEADER_LENGTH = 7; // TODO generated instead

declare module '../generated/service' {
  export interface InterfaceService {
    test<T extends ExecuteMutlipleCallItem<any, any>[]>(input: T): any;

    // TODO it could maybe factorized
    executeMultipleCalls<T1>(
      calls: [ExecuteMutlipleCallItem<any, T1>],
    ): Promise<[TapResponse<T1>]>;
    executeMultipleCalls<T1, T2>(
      calls: [
        ExecuteMutlipleCallItem<any, T1>,
        ExecuteMutlipleCallItem<any, T2>,
      ],
    ): Promise<[TapResponse<T1>, TapResponse<T2>]>;
    executeMultipleCalls<T1, T2, T3>(
      calls: [
        ExecuteMutlipleCallItem<any, T1>,
        ExecuteMutlipleCallItem<any, T2>,
        ExecuteMutlipleCallItem<any, T3>,
      ],
    ): Promise<[TapResponse<T1>, TapResponse<T2>, TapResponse<T3>]>;
    executeMultipleCalls<T1, T2, T3, T4>(
      calls: [
        ExecuteMutlipleCallItem<any, T1>,
        ExecuteMutlipleCallItem<any, T2>,
        ExecuteMutlipleCallItem<any, T3>,
        ExecuteMutlipleCallItem<any, T4>,
      ],
    ): Promise<
      [TapResponse<T1>, TapResponse<T2>, TapResponse<T3>, TapResponse<T4>]
    >;
    executeMultipleCalls<T1, T2, T3, T4, T5>(
      calls: [
        ExecuteMutlipleCallItem<any, T1>,
        ExecuteMutlipleCallItem<any, T2>,
        ExecuteMutlipleCallItem<any, T3>,
        ExecuteMutlipleCallItem<any, T4>,
        ExecuteMutlipleCallItem<any, T5>,
      ],
    ): Promise<
      [
        TapResponse<T1>,
        TapResponse<T2>,
        TapResponse<T3>,
        TapResponse<T4>,
        TapResponse<T5>,
      ]
    >;
    executeMultipleCalls<T1, T2, T3, T4, T5, T6>(
      calls: [
        ExecuteMutlipleCallItem<any, T1>,
        ExecuteMutlipleCallItem<any, T2>,
        ExecuteMutlipleCallItem<any, T3>,
        ExecuteMutlipleCallItem<any, T4>,
        ExecuteMutlipleCallItem<any, T5>,
        ExecuteMutlipleCallItem<any, T6>,
      ],
    ): Promise<
      [
        TapResponse<T1>,
        TapResponse<T2>,
        TapResponse<T3>,
        TapResponse<T4>,
        TapResponse<T5>,
        TapResponse<T6>,
      ]
    >;
    executeMultipleCalls<T1, T2, T3, T4, T5, T6, T7>(
      calls: [
        ExecuteMutlipleCallItem<any, T1>,
        ExecuteMutlipleCallItem<any, T2>,
        ExecuteMutlipleCallItem<any, T3>,
        ExecuteMutlipleCallItem<any, T4>,
        ExecuteMutlipleCallItem<any, T5>,
        ExecuteMutlipleCallItem<any, T6>,
        ExecuteMutlipleCallItem<any, T7>,
      ],
    ): Promise<
      [
        TapResponse<T1>,
        TapResponse<T2>,
        TapResponse<T3>,
        TapResponse<T4>,
        TapResponse<T5>,
        TapResponse<T6>,
        TapResponse<T7>,
      ]
    >;
    executeMultipleCalls<T extends ExecuteMutlipleCallItem<any, any>[]>(
      calls: T,
    ): Promise<TapResponse<unknown>[]>;
  }
}

function toBodyDecoder<T = any>(
  input: DecoderFct<Uint8Array, T> | undefined | BodyDecoder<T>,
): BodyDecoder<T> | undefined {
  if (typeof input === 'function') {
    return {
      decode: input,
    };
  } else {
    return input;
  }
}

function toCall<BodyType, ReturnType>(
  serviceCall: ExecuteMutlipleCallItem<BodyType, ReturnType>,
): ServiceCallType<BodyType, ReturnType> {
  if ('expectedResponseStatus' in serviceCall) {
    return serviceCall.call;
  } else {
    return serviceCall;
  }
}

InterfaceService.prototype.executeMultipleCalls = async function (
  calls: ExecuteMutlipleCallItem<any, any>[],
  defaultExpectedResponseStatus: MultiRequestFrame.ExpectedResponseStatus = MultiRequestFrame
    .ExpectedResponseStatus.ANYTHING,
) {
  const decoders: (BodyDecoder<any> | undefined)[] = calls.map((serviceCall) =>
    toBodyDecoder(toCall(serviceCall).responseBodyDecoder),
  );
  const multiRequestRequests = createMultiRequestItems(
    calls,
    defaultExpectedResponseStatus,
  );
  const multiRequestFrame: MultiRequestFrame = {
    requests: multiRequestRequests.map((item) => item.requestItem),
  };
  const response = await this.executeMultiRequestFrame(multiRequestFrame);
  const body: MultiResponseFrame = response.body();
  if (body.responses.length !== calls.length) {
    throw new Error(
      `API sent ${calls.length} request(s) but tap responded with ${body.responses.length} response(s).`,
    );
  }
  const responses: TapResponse<any>[] = body.responses.map(
    (tapResponseItem, index) => {
      const response = new TapResponse(tapResponseItem.response);
      const decoder = decoders[index];
      if (decoder) {
        response.setBodyDecoder(decoder);
      }
      response.setRequest(multiRequestRequests[index].requestItem.request);
      if (!response.isSuccessful()) {
        response.setError(
          TapError.reponseStatusError(response, toCall(calls[index])),
        );
      }
      return response;
    },
  );
  return responses as any;
};

export function createMultiRequestItems(
  calls: ExecuteMutlipleCallItem<any, any>[],
  defaultExpectedResponseStatus: MultiRequestFrame.ExpectedResponseStatus = MultiRequestFrame
    .ExpectedResponseStatus.ANYTHING,
): {
  requestItem: MultiRequestFrame.RequestItem;
  responseBodyDecoder?: DecoderFct<Uint8Array, unknown>;
}[] {
  return calls.map((serviceCall) => {
    let call: ServiceCallType<unknown, unknown>;
    let expectedResponseStatus: number;
    if ('expectedResponseStatus' in serviceCall) {
      call = serviceCall.call;
      expectedResponseStatus = serviceCall.expectedResponseStatus;
    } else {
      call = serviceCall;
      expectedResponseStatus = defaultExpectedResponseStatus;
    }
    const request = ServiceCallRunner.toTapRequest(call);
    const responseBodyDecoder =
      ServiceCallRunner.resolveResponseBodyDecoder(call);
    return {
      requestItem: {
        expectedResponseStatus,
        request,
        requestLen: request.payload.length + TAP_REQUEST_FRAME_HEADER_LENGTH,
      },
      responseBodyDecoder,
    };
  });
}
